import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { PdfHeader } from "./PdfHeader";
import { DataSpecPdf } from "../dataspec/export/DataSpecPdf";

const styles = StyleSheet.create({


    page: {
        color: "#3A3536",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        paddingTop: 20,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 60
    },

    card: {
        backgroundColor: "#FFFFFF",
        borderWidth: 1,
        borderColor: "#E5E7EB",
        padding: 18,
        marginBottom: 20
    },

    title: {
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 16,
        color: "#111827"
    },

    row: {
        flexDirection: "row",
        marginBottom: 12
    },

    field: {
        flex: 1,
        paddingRight: 20
    },

    label: {
        fontSize: 8,
        color: "#6B7280",
        textTransform: "uppercase"
    },

    value: {
        fontSize: 11,
        marginTop: 4,
        color: "#374151"
    },

    tableTitle: {
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 10
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#F3F4F6",
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: "#E5E7EB",
        paddingVertical: 8
    },

    headerText: {
        fontWeight: "bold",
        fontSize: 9,
        color: "#374151"
    },

    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderColor: "#E5E7EB",
        paddingVertical: 8
    },

    cell: {
        paddingHorizontal: 6,
        fontSize: 12
    },

    colNumber: { width: 35 },
    colJob: { flex: 1.5 },
    colDataSpec: { flex: 2 },
    colItemName: { flex: 3 },
    colQty: { width: 50, textAlign: "right" },

    dataSpecText: {
        fontSize: 10,
        fontWeight: "bold",
        color: "#F97316"
    },

    itemText: {
        fontSize: 10,
        color: "#374151"
    },

    footer: {
        position: "absolute",
        bottom: 20,
        left: 30,
        right: 30,
        textAlign: "center",
        fontSize: 8,
        color: "#9CA3AF"
    }

});


export const OrderPdf = ({ order = {}, items = [], dataSpecs = [], users=[], customers=[],
                             transponderModels, printTypes, dataRecordTypes, uidFormats, dataListSources }) => {

    const generatedDate = new Date().toLocaleString();
    // remove duplicate dataspecs
    const uniqueDataSpecs = [
        ...new Map(dataSpecs.map(ds => [ds.id, ds])).values()
    ];

    return (
        <Document>

            {/* ORDER PAGE */}

            <Page size="A4" orientation="landscape" style={styles.page} >

                <PdfHeader title={"Order Summary"} />

                {/* DETAILS */}

                <View style={styles.card}>

                    <Text style={styles.title}>Details</Text>

                    <View style={styles.row}>

                        <View style={styles.field}>
                            <Text style={styles.label}>Sales Order Number</Text>
                            <Text style={styles.value}>
                                {order?.vtiger_salesorder_no || "-"}
                            </Text>
                        </View>

                        <View style={styles.field}>
                            <Text style={styles.label}>Organization</Text>
                            <Text style={styles.value}>
                                {order?.customer_name ||
                                    customers?.data?.find(c => c.id === order?.customer_id)?.name ||
                                    "-"}
                            </Text>
                        </View>

                        <View style={styles.field}>
                            <Text style={styles.label}>Status</Text>
                            <Text style={styles.value}>
                                {order?.vtiger_sostatus || "-"}
                            </Text>
                        </View>

                    </View>

                    <View style={styles.row}>
                        <View style={styles.field}>
                            <Text style={styles.label}>Data Status</Text>
                            <Text style={styles.value}>
                                {order?.vtiger_datastatus || "-"}
                            </Text>
                        </View>
                    </View>

                </View>


                {/* LINE ITEMS */}

                <Text style={styles.tableTitle}>Line Items</Text>

                <View style={styles.tableHeader}>
                    <Text style={[styles.colNumber, styles.cell, styles.headerText]}>#</Text>
                    <Text style={[styles.colJob, styles.cell, styles.headerText]}>Job Name</Text>
                    <Text style={[styles.colDataSpec, styles.cell, styles.headerText]}>Data Spec</Text>
                    <Text style={[styles.colItemName, styles.cell, styles.headerText]}>Item Name</Text>
                    <Text style={[styles.colQty, styles.cell, styles.headerText]}>Qty</Text>
                </View>

                {items.map((item) => (

                    <View style={styles.tableRow} key={item.id || item.number} wrap={false}>

                        <Text style={[styles.colNumber, styles.cell]}>
                            {item?.number || "-"}
                        </Text>

                        <Text style={[styles.colJob, styles.cell]}>
                            {item?.job_name || "-"}
                        </Text>

                        <Text style={[styles.colDataSpec, styles.cell, styles.dataSpecText]}>
                            {item?.data_spec_number || "-"}
                        </Text>

                        <Text style={[styles.colItemName, styles.cell, styles.itemText]}>
                            <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                                {item?.product_name} {"\n"}
                            </Text>
                            {item?.details || "-"}
                        </Text>

                        <Text style={[styles.colQty, styles.cell]}>
                            {item?.quantity || "-"}
                        </Text>

                    </View>

                ))}

                <Text style={styles.footer}>
                    Generated on {generatedDate}
                </Text>

            </Page>


            {/* DATASPEC PAGES */}

            {uniqueDataSpecs.map((ds) => (

                <DataSpecPdf
                    key={ds.id}
                    standalone={false}
                    dataSpec={ds}
                    customer={customers.data?.find(c => c.id === ds.customer_id)}
                    assignedTo={users.data?.find(u => u.id === ds.assigned_to_id)}
                    printConfig={ds.printConfig}
                    transponderModels={transponderModels}
                    printTypes={printTypes}
                    dataRecordTypes={dataRecordTypes}
                    uidFormats={uidFormats}
                    dataListSources={dataListSources}
                    chipConfigs={ds.transponderConfigs || []}
                    dataListColumns={ds.dataListColumns || []}
                    factoryInstructions={ds.factoryInstructions}
                />

            ))}


        </Document>
    );
};
